import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Hash, Quote, AlertTriangle, Settings2 } from 'lucide-react';
import api from '../api';
import VoceBrandModal from './VoceBrandModal';

const MAX_CAPTION = 2200;   // limite Instagram
const MAX_HASHTAG = 30;

/**
 * Textarea della caption con la "voce del brand" del cliente:
 * inserimento rapido di set di hashtag e snippet, avvisi su parole vietate e limiti.
 */
export default function CaptionEditor({ clienteId, value, onChange, disabled, placeholder }) {
  const [voce, setVoce] = useState(null);
  const [menu, setMenu] = useState(null);      // 'hashtag' | 'snippet' | null
  const [gestisci, setGestisci] = useState(false);
  const ref = useRef(null);

  const load = () => {
    if (!clienteId) return;
    api.get(`/voce/${clienteId}`, { silent: true }).then(r => setVoce(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [clienteId]);

  const testo = value || '';
  const hashtags = (testo.match(/#[\p{L}\p{N}_]+/gu) || []).length;
  const vietate = useMemo(() => {
    const low = testo.toLowerCase();
    return (voce?.parole_vietate || []).filter(p => p && low.includes(p.toLowerCase()));
  }, [testo, voce]);

  const inserisci = (t) => {
    const el = ref.current;
    const start = el ? el.selectionStart : testo.length;
    const end = el ? el.selectionEnd : testo.length;
    const prima = testo.slice(0, start), dopo = testo.slice(end);
    const sep = prima && !/\s$/.test(prima) ? (t.startsWith('#') ? '\n\n' : ' ') : '';
    const nuovo = prima + sep + t + dopo;
    onChange(nuovo);
    setMenu(null);
    setTimeout(() => { if (el) { el.focus(); const pos = (prima + sep + t).length; el.setSelectionRange(pos, pos); } }, 0);
  };

  const chip = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 7, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`, background: active ? 'var(--cyan-dim)' : 'var(--surface)', color: active ? 'var(--cyan)' : 'var(--text-muted)',
  });

  return (
    <div>
      {voce?.tono && !disabled && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', background: 'var(--bg3)', borderRadius: 8, padding: '6px 10px', marginBottom: 6 }}>
          <b style={{ color: 'var(--text)' }}>Tono di voce:</b> {voce.tono}
        </div>
      )}
      <textarea ref={ref} className="eden-input" value={testo} onChange={e => onChange(e.target.value)} disabled={disabled}
        placeholder={placeholder} style={{ minHeight: 110, resize: 'vertical', opacity: disabled ? 0.5 : 1 }} />
      {!disabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap', position: 'relative' }}>
          <button type="button" onClick={() => setMenu(m => m === 'hashtag' ? null : 'hashtag')} style={chip(menu === 'hashtag')}><Hash size={12} /> Hashtag</button>
          <button type="button" onClick={() => setMenu(m => m === 'snippet' ? null : 'snippet')} style={chip(menu === 'snippet')}><Quote size={12} /> Snippet</button>
          <button type="button" onClick={() => setGestisci(true)} style={chip(false)} title="Tono, parole vietate, hashtag e snippet del cliente"><Settings2 size={12} /> Voce del brand</button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: testo.length > MAX_CAPTION ? 'var(--red)' : 'var(--text-dim)' }}>
            {testo.length}/{MAX_CAPTION}
          </span>
          <span style={{ fontSize: 11, color: hashtags > MAX_HASHTAG ? 'var(--red)' : 'var(--text-dim)' }}>#{hashtags}/{MAX_HASHTAG}</span>

          {menu && (
            <div style={{ position: 'absolute', top: 32, left: 0, zIndex: 20, width: 'min(420px, 100%)', maxHeight: 280, overflowY: 'auto',
              background: 'var(--bg2, #14141c)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.4)', padding: 6 }}>
              {(menu === 'hashtag' ? voce?.hashtag_set : voce?.snippet || [])?.length ? (
                (menu === 'hashtag' ? voce.hashtag_set : voce.snippet).map((x, i) => (
                  <button key={i} type="button" onClick={() => inserisci(menu === 'hashtag' ? x.tags : x.testo)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', background: 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{x.nome}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{menu === 'hashtag' ? x.tags : x.testo}</div>
                  </button>
                ))
              ) : (
                <div style={{ padding: 10, fontSize: 12, color: 'var(--text-dim)' }}>
                  Nessun {menu === 'hashtag' ? 'set di hashtag' : 'snippet'} per questo cliente.{' '}
                  <button type="button" onClick={() => { setMenu(null); setGestisci(true); }} style={{ background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer', padding: 0, fontSize: 12 }}>Aggiungine</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!disabled && vietate.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--red)' }}>
          <AlertTriangle size={13} /> Parole da evitare per questo cliente: {vietate.join(', ')}
        </div>
      )}
      <VoceBrandModal open={gestisci} clienteId={clienteId} onClose={() => setGestisci(false)} onSaved={v => setVoce(v)} />
    </div>
  );
}
